import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Card, Skeleton } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { surfaceCardSx } from "@/components/screens/ServiceDashboard/components/styles";

import VariationBadge from "../VariationBadge";

type Props = {
  title: string;
  aiLabel: string;
  humanLabel: string;
  aiCount: number;
  humanCount: number;
  variation: number | null | undefined;
  caption: string;
  aiColor: string;
  humanColor: string;
  isLoading?: boolean;
};

const RADIUS = 30;
const CIRC = 2 * Math.PI * RADIUS;
const fmt = (value: number) => `${Math.round(value)}%`;

const AiDonutTile = ({
  title,
  aiLabel,
  humanLabel,
  aiCount,
  humanCount,
  variation,
  caption,
  aiColor,
  humanColor,
  isLoading,
}: Props) => {
  const total = aiCount + humanCount;
  const aiPct = total ? (aiCount / total) * 100 : 0;
  const humanPct = total ? 100 - aiPct : 0;

  return (
    <Card sx={surfaceCardSx()}>
      <MDBox p={1.5} display="flex" flexDirection="column" height="100%">
        <MDBox display="flex" alignItems="center" gap={1}>
          <SmartToyIcon sx={{ color: aiColor, fontSize: "1.6rem !important" }} />
          <MDTypography variant="caption" fontWeight="medium" color="dark">
            {title}
          </MDTypography>
        </MDBox>
        {isLoading ? (
          <Skeleton variant="circular" width={72} height={72} sx={{ m: "0.5rem auto" }} />
        ) : (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
            <MDBox position="relative" width={76} height={76}>
              <svg viewBox="0 0 76 76" width="76" height="76">
                <circle
                  cx="38"
                  cy="38"
                  r={RADIUS}
                  fill="none"
                  stroke={humanColor}
                  strokeWidth="9"
                />
                <circle
                  cx="38"
                  cy="38"
                  r={RADIUS}
                  fill="none"
                  stroke={aiColor}
                  strokeWidth="9"
                  strokeDasharray={`${(aiPct / 100) * CIRC} ${CIRC}`}
                  transform="rotate(-90 38 38)"
                  style={{ transition: "stroke-dasharray 900ms ease" }}
                />
              </svg>
              <MDBox
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <MDTypography variant="button" fontWeight="bold" lineHeight={1}>
                  {fmt(aiPct)}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: aiColor, fontSize: "0.6rem" }}>
                  {aiLabel}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox display="flex" flexDirection="column" gap={0.5}>
              {[
                { label: aiLabel, pct: aiPct, color: aiColor },
                { label: humanLabel, pct: humanPct, color: humanColor },
              ].map((item) => (
                <MDBox key={item.label} display="flex" gap={0.5}>
                  <MDBox
                    width="0.55rem"
                    height="0.55rem"
                    mt={0.5}
                    borderRadius={"50%" as never}
                    sx={{ backgroundColor: item.color }}
                  />
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      lineHeight={1.2}
                    >
                      {fmt(item.pct)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" lineHeight={1.2}>
                      {item.label}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        )}
        <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <MDTypography variant="caption" color="text">
            {caption}:
          </MDTypography>
          <VariationBadge value={variation} isPoints />
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default AiDonutTile;
